import { apiGet, apiPost, apiPut } from '../../lib/api'
import { withAuth } from '../auth/token'

/** One person on a broker's list, as the agent slices answer it. */
export interface BrokerAgent {
  id: string
  brokerId: string
  firstName: string
  lastName: string
  title: string | null
  /**
   * Required, with the phone: every agent on the list can be emailed and called.
   * Lower-cased by the API — one inbox is one agent, however it was typed.
   */
  email: string
  /** Formatted for reading and dialling, "(513) 495-2000". */
  phone: string
  /** The digits dialled after the switchboard answers. */
  phoneExtension: string | null
  optedOutOfBroadcasts: boolean
  /** When the person asked not to receive availability emails. */
  broadcastOptedOutAt: string | null
  active: boolean
  deactivatedAt: string | null
  createdAt: string
  /**
   * The version an edit is made on: the panel sends it back so a form opened before
   * somebody else's save is refused instead of quietly undoing it (review finding on
   * #24). Only a save of this agent's form moves it.
   */
  updatedAt: string
}

/**
 * What the agent form posts, add and edit alike. The contact fields stay nullable
 * here although the answer's are not: an empty box is what the required-field rules
 * exist to refuse, and a payload that could not express one would take the refusal
 * out of reach of the tests.
 */
export interface BrokerAgentPayload {
  firstName: string
  lastName: string
  title: string | null
  email: string | null
  phone: string | null
  phoneExtension: string | null
  optedOutOfBroadcasts: boolean
}

const agentsPath = (brokerId: string) => `/api/brokers/${brokerId}/agents`

export function listAgents(brokerId: string): Promise<BrokerAgent[]> {
  return apiGet<BrokerAgent[]>(agentsPath(brokerId), { headers: withAuth() })
}

export function addAgent(brokerId: string, payload: BrokerAgentPayload): Promise<BrokerAgent> {
  return apiPost<BrokerAgent>(agentsPath(brokerId), payload, { headers: withAuth() })
}

/**
 * Saves an edit made on the agent as it was at `version` — the `updatedAt` the form was
 * opened on. A newer save in between answers `record_changed_elsewhere`.
 */
export function updateAgent(
  brokerId: string,
  agentId: string,
  payload: BrokerAgentPayload,
  version: string,
): Promise<BrokerAgent> {
  return apiPut<BrokerAgent>(
    `${agentsPath(brokerId)}/${agentId}`,
    { ...payload, version },
    { headers: withAuth() },
  )
}

export function setAgentActivation(
  brokerId: string,
  agentId: string,
  active: boolean,
): Promise<BrokerAgent> {
  return apiPut<BrokerAgent>(
    `${agentsPath(brokerId)}/${agentId}/activation`,
    { active },
    { headers: withAuth() },
  )
}
